
import { Component, Vue, Watch } from 'vue-property-decorator';
import { ChurnMerchantListModel } from '@/models/churn_merchant_list';
import { ChurnMerchant } from '@/types/churn';
import { BillingPackageList } from '@/collections/billing_packages';

@Component({})
export default class ChurnMerchants extends Vue {
    public model: ChurnMerchantListModel = new ChurnMerchantListModel();
    public billingPackages: BillingPackageList = new BillingPackageList();
    public isLoading: boolean = false;
    public error: string = '';
    public selectedTier: string | null = null;
    public selectedBillingPackage: number | null = null;
    public searchQuery: string = '';

    public tableOptions: any = {
        page: 1,
        itemsPerPage: 50,
        sortBy: ['churn_probability'],
        sortDesc: [true],
    };

    public headers = [
        { text: 'Store', value: 'store_id', sortable: true, width: '15%' },
        { text: 'Package', value: 'billing_package_name', sortable: false, width: '20%' },
        { text: 'Risk Tier', value: 'risk_tier', sortable: true, width: '15%' },
        { text: 'Churn Probability', value: 'churn_probability', sortable: true, width: '30%' },
        { text: 'Prediction Date', value: 'prediction_date', sortable: true, width: '20%' },
    ];

    public tierOptions = [
        { text: 'Critical', value: 'critical' },
        { text: 'High Risk', value: 'high' },
        { text: 'Medium Risk', value: 'medium' },
        { text: 'Low Risk', value: 'low' },
    ];

    public tierColors: Record<string, string> = {
        critical: '#DC2626',
        high: '#F59E0B',
        medium: '#3B82F6',
        low: '#10B981',
    };

    public tierLabels: Record<string, string> = {
        critical: 'Critical',
        high: 'High',
        medium: 'Medium',
        low: 'Low',
    };

    public created(): void {
        const tierParam = this.$route.query.tier as string;
        if (tierParam && ['critical', 'high', 'medium', 'low'].includes(tierParam)) {
            this.selectedTier = tierParam;
        }
        const pkgParam = this.$route.query.billing_package_id as string;
        if (pkgParam) {
            this.selectedBillingPackage = parseInt(pkgParam);
        }
    }

    public mounted(): void {
        this.fetchBillingPackages();
        this.fetchMerchants();
    }

    public get billingPackageOptions(): Array<{ text: string; value: number }> {
        return this.billingPackages.items.map((pkg) => ({
            text: `${pkg.name} ($${pkg.price})`,
            value: pkg.id,
        }));
    }

    public async fetchBillingPackages(): Promise<void> {
        try {
            await this.billingPackages.fetch();
        } catch (err: any) {
            console.error('Failed to load billing packages:', err);
        }
    }

    @Watch('tableOptions', { deep: true })
    public onTableOptionsChange(): void {
        this.fetchMerchants();
    }

    public onTierChange(): void {
        this.tableOptions.page = 1;
        this.fetchMerchants();
    }

    public onBillingPackageChange(): void {
        this.tableOptions.page = 1;
        this.fetchMerchants();
    }

    public onSearch(): void {
        this.tableOptions.page = 1;
        this.fetchMerchants();
    }

    public onSearchClear(): void {
        this.searchQuery = '';
        this.tableOptions.page = 1;
        this.fetchMerchants();
    }

    public async fetchMerchants(): Promise<void> {
        this.isLoading = true;
        this.error = '';
        try {
            const offset = (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage;
            await this.model.fetch({
                tier: this.selectedTier || undefined,
                billing_package_id: this.selectedBillingPackage || undefined,
                limit: this.tableOptions.itemsPerPage,
                offset,
                sort_by: this.tableOptions.sortBy[0] || 'churn_probability',
                sort_order: this.tableOptions.sortDesc[0] ? 'DESC' : 'ASC',
            });
        } catch (err: any) {
            this.error = err.message || 'Failed to load merchants';
        } finally {
            this.isLoading = false;
        }
    }

    public formatDate(dateStr: string): string {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
        });
    }

    public getTierIcon(tier: string): string {
        const icons: Record<string, string> = {
            critical: 'mdi-alert-octagon',
            high: 'mdi-alert-circle',
            medium: 'mdi-alert',
            low: 'mdi-check-circle',
        };
        return icons[tier] || 'mdi-help-circle';
    }

    public getProgressColor(probability: number): string {
        if (probability >= 80) return '#DC2626';
        if (probability >= 60) return '#F59E0B';
        if (probability >= 40) return '#3B82F6';
        return '#10B981';
    }
}
